import React, { useState, useEffect, useRef } from 'react';
import bulgarianLabels from '../utils/bulgarianLabels.json';

const ResultsDisplay = ({ results, onReset, onBackToValidation }) => {
  const [activeTab, setActiveTab] = useState('extracted');
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const tableRef = useRef(null);

  // Add column resizing functionality
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    const resizers = table.querySelectorAll('.column-resizer');
    let isResizing = false;
    let currentResizer = null;
    let startX = 0;
    let startWidth = 0;

    const handleMouseDown = (e) => {
      isResizing = true;
      currentResizer = e.target;
      startX = e.clientX;
      const th = currentResizer.parentElement;
      startWidth = parseInt(window.getComputedStyle(th).width, 10);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      e.preventDefault();
    };

    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const th = currentResizer.parentElement;
      const width = startWidth + e.clientX - startX;
      th.style.width = width + 'px';
    };

    const handleMouseUp = () => {
      isResizing = false;
      currentResizer = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    resizers.forEach(resizer => {
      resizer.addEventListener('mousedown', handleMouseDown);
    });

    return () => {
      resizers.forEach(resizer => {
        resizer.removeEventListener('mousedown', handleMouseDown);
      });
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeTab]);

  if (!results || !results.success) {
    return (
      <div className="results-container">
        <h3>❌ Грешка при обработка / Processing Error</h3>
        <p>No valid results to display.</p>
        <button onClick={onReset} className="reset-button">
          Опитай отново / Try Again
        </button>
      </div>
    );
  }

  // Handle download with proper URL construction
  const handleDownload = (sessionId, type) => {
    // In development, use the full server URL; in production, use relative URL
    const serverUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
    const downloadUrl = `${serverUrl}/api/download/${sessionId}?type=${type}`;
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = ''; // This will use the filename from the server
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  console.log('ResultsDisplay received:', { results, type: results?.type, data: results?.data });

  const { data, processing, type, sessionId: resultSessionId } = results;

  // Handle different result types
  let filesData = [];
  let isMultiFile = false;
  let sessionId = null;

  console.log('Processing data:', { type, data, resultSessionId });

  if (type === 'multi-file' && Array.isArray(data)) {
    // Multi-file upload results
    isMultiFile = true;
    sessionId = resultSessionId || results.sessionId;
    console.log('Multi-file detected, sessionId:', sessionId);
    
    filesData = data.map((file, index) => {
      console.log(`Processing file ${index}:`, file);
      return {
        index,
        fileName: file._fileName || file.fileName || `File ${index + 1}`,
        extractedFields: file,
        ocrResult: { 
          confidence: file._confidence || 0.9, 
          text: file._ocrText || 'OCR text not available in summary' 
        },
        analysis: file._analysis || { 
          completenessScore: 85, 
          issues: [], 
          summary: 'Processed successfully' 
        },
        metadata: file._metadata || { 
          fileType: file._fileType || 'unknown',
          originalFile: file._originalFile || file._fileName,
          processingTime: file._metadata?.processingTime || 5000,
          conversationId: file._metadata?.conversationId,
          originalTextLength: file._metadata?.originalTextLength
        }
      };
    });
  } else if (type === 'multi-invoice' && data?.invoices) {
    // Multi-invoice PDF results (deprecated but might still exist)
    isMultiFile = true;
    sessionId = data.sessionId || resultSessionId;
    console.log('Multi-invoice detected, sessionId:', sessionId);
    
    filesData = data.invoices.map((invoice, index) => ({
      index,
      fileName: invoice.fileName || `Page ${index + 1}`,
      extractedFields: invoice.extractedFields || invoice,
      ocrResult: invoice.ocrResult || { confidence: 0.9, text: 'OCR text not available' },
      analysis: invoice.analysis || { completenessScore: 85, issues: [], summary: 'Processed successfully' },
      metadata: invoice.metadata || { processingTime: 5000 }
    }));
  } else if (type === 'single-invoice' || data) {
    // Single file result
    console.log('Single invoice detected');
    const { ocrResult, extractedFields, analysis, metadata } = data || {};
    sessionId = resultSessionId || null;
    
    filesData = [{
      index: 0,
      fileName: processing?.filename || extractedFields?.fileName || 'Single Invoice',
      extractedFields,
      ocrResult,
      analysis,
      metadata
    }];
  } else {
    console.warn('Unknown data structure:', { type, data });
  }

  console.log('Final filesData:', filesData);
  console.log('Final sessionId:', sessionId);

  // Get current file data
  const currentFile = filesData[selectedFileIndex] || filesData[0];

  const getConfidenceClass = (confidence) => {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.6) return 'confidence-medium';
    return 'confidence-low';
  };

  const getConfidenceText = (confidence) => {
    const percentage = Math.round(confidence * 100);
    if (percentage >= 80) return `${percentage}% (Високо / High)`;
    if (percentage >= 60) return `${percentage}% (Средно / Medium)`;
    return `${percentage}% (Ниско / Low)`;
  };

  const renderFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="field-value null">Не е намерено / Not found</span>;
    }
    
    if (Array.isArray(value)) {
      return (
        <div className="field-value">
          {value.map((item, index) => (
            <div key={index} style={{ marginBottom: '0.5rem' }}>
              {typeof item === 'object' ? JSON.stringify(item, null, 2) : item}
            </div>
          ))}
        </div>
      );
    }
    
    return <div className="field-value">{value.toString()}</div>;
  };

  // Field labels mapping using Bulgarian labels file
  const fieldLabels = {
    supplierName: `${bulgarianLabels.supplierName} / Supplier Name`,
    supplierVatNumber: `${bulgarianLabels.supplierVatNumber} / Supplier VAT Number`,
    supplierAddress: `${bulgarianLabels.supplierAddress} / Supplier Address`,
    supplierId: `${bulgarianLabels.supplierId} / Supplier ID/EIK`,
    supplierManager: `${bulgarianLabels.supplierManager} / Supplier Manager`,
    customerName: `${bulgarianLabels.customerName} / Customer Name`,
    customerVatNumber: `${bulgarianLabels.customerVatNumber} / Customer VAT Number`,
    customerAddress: `${bulgarianLabels.customerAddress} / Customer Address`,
    customerId: `${bulgarianLabels.customerId} / Customer ID/EIK`,
    invoiceNumber: `${bulgarianLabels.invoiceNumber} / Invoice Number`,
    invoiceDate: `${bulgarianLabels.invoiceDate} / Invoice Date`,
    dueDate: `${bulgarianLabels.dueDate} / Due Date`,
    currency: `${bulgarianLabels.currency} / Currency`,
    subtotalAmount: `${bulgarianLabels.subtotalAmount} / Subtotal`,
    vatAmount: `${bulgarianLabels.vatAmount} / VAT Amount`,
    vatRate: `${bulgarianLabels.vatRate} / VAT Rate`,
    totalAmount: `${bulgarianLabels.totalAmount} / Total Amount`,
    paymentMethod: `${bulgarianLabels.paymentMethod} / Payment Method`,
    bankAccount: `${bulgarianLabels.bankAccount} / Bank Account`,
    description: `${bulgarianLabels.description} / Description`,
    notes: `${bulgarianLabels.notes} / Notes`,
    fileName: `${bulgarianLabels.fileName} / File Name`
  };

  const renderTabContent = () => {
    if (!currentFile) {
      console.warn('No currentFile available');
      return <div>No file data available</div>;
    }

    const { extractedFields, ocrResult, analysis, metadata } = currentFile;
    console.log('Current file data:', { extractedFields, ocrResult, analysis, metadata });

    switch (activeTab) {
      case 'extracted':
        console.log('Rendering extracted fields:', extractedFields);
        const fieldsToRender = Object.entries(extractedFields || {}).filter(([key]) => !key.startsWith('_') && key !== 'fileName');
        console.log('Fields to render:', fieldsToRender);
        
        return (
          <div className="extracted-fields">
            <div className="field-table-container">
              <table className="field-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th className="resizable-column" style={{ width: '30%' }}>
                      Поле / Field
                      <div className="column-resizer"></div>
                    </th>
                    <th className="resizable-column" style={{ width: '70%' }}>
                      Стойност / Value
                      <div className="column-resizer"></div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fieldsToRender.length === 0 ? (
                    <tr>
                      <td colSpan="2" style={{ textAlign: 'center', padding: '2rem', fontStyle: 'italic' }}>
                        Няма данни за показване / No data to display
                      </td>
                    </tr>
                  ) : (
                    fieldsToRender.map(([key, value]) => {
                      const label = fieldLabels[key] || key;
                      return (
                        <tr key={key} className="field-row">
                          <td className="field-label">{label}</td>
                          <td className="field-value-cell">{renderFieldValue(value)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'ocr':
        return (
          <div className="ocr-content">
            <div className="confidence-badge">
              <span className={`confidence ${getConfidenceClass(ocrResult?.confidence || 0)}`}>
                Увереност / Confidence: {getConfidenceText(ocrResult?.confidence || 0)}
              </span>
            </div>
            <div className="ocr-text">
              <pre>{ocrResult?.text || 'OCR text not available'}</pre>
            </div>
          </div>
        );

      case 'analysis':
        return (
          <div className="analysis-content">
            <div className="analysis-summary">
              <h4>📊 Резюме на анализа / Analysis Summary</h4>
              <p>{analysis?.summary || 'No analysis summary available'}</p>
            </div>
            <div className="completeness-score">
              <h4>📈 Резултат за пълнота / Completeness Score</h4>
              <div className="score-bar">
                <div 
                  className="score-fill" 
                  style={{ width: `${analysis?.completenessScore || 0}%` }}
                ></div>
                <span className="score-text">{analysis?.completenessScore || 0}%</span>
              </div>
            </div>
            {analysis?.issues && analysis.issues.length > 0 && (
              <div className="issues-list">
                <h4>⚠️ Проблеми / Issues</h4>
                <ul>
                  {analysis.issues.map((issue, index) => (
                    <li key={index}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}
            {analysis?.recommendations && analysis.recommendations.length > 0 && (
              <div className="recommendations-list">
                <h4>💡 Препоръки / Recommendations</h4>
                <ul>
                  {analysis.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'metadata':
        return (
          <div className="metadata-content">
            <div className="metadata-grid">
              <div className="metadata-row">
                <label>Време за обработка / Processing Time:</label>
                <span>{metadata?.processingTime || 'N/A'} ms</span>
              </div>
              <div className="metadata-row">
                <label>Тип файл / File Type:</label>
                <span>{metadata?.fileType || 'unknown'}</span>
              </div>
              <div className="metadata-row">
                <label>Оригинален файл / Original File:</label>
                <span>{metadata?.originalFile || currentFile.fileName}</span>
              </div>
              {metadata?.conversationId && (
                <div className="metadata-row">
                  <label>ID на разговор / Conversation ID:</label>
                  <span className="conversation-id">{metadata.conversationId}</span>
                </div>
              )}
              {metadata?.originalTextLength && (
                <div className="metadata-row">
                  <label>Дължина на текст / Text Length:</label>
                  <span>{metadata.originalTextLength} characters</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'download':
        return (
          <div className="download-content">
            {sessionId ? (
              <div className="download-options">
                <h4>📥 Налични файлове за изтегляне / Available Downloads</h4>
                <div className="download-grid">
                  <div className="download-item">
                    <div className="download-icon">📊</div>
                    <div className="download-info">
                      <h5>Excel файл / Excel File</h5>
                      <p>Структурирани данни в Excel формат / Structured data in Excel format</p>
                      <button 
                        onClick={() => handleDownload(sessionId, 'excel')}
                        className="download-button excel"
                      >
                        Изтегли Excel / Download Excel
                      </button>
                    </div>
                  </div>
                  <div className="download-item">
                    <div className="download-icon">📦</div>
                    <div className="download-info">
                      <h5>ZIP архив / ZIP Archive</h5>
                      <p>Excel файл + оригинални файлове / Excel file + original files</p>
                      <button 
                        onClick={() => handleDownload(sessionId, 'zip')}
                        className="download-button zip"
                      >
                        Изтегли ZIP / Download ZIP
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-downloads">
                <p>🚫 Няма налични файлове за изтегляне / No downloads available</p>
                <p>Файловете за изтегляне са достъпни само за multi-file обработка / Downloads are only available for multi-file processing</p>
              </div>
            )}
          </div>
        );

      default:
        return <div>Select a tab to view content</div>;
    }
  };

  return (
    <div className="results-container">
      <div className="results-header">
        <h2>📋 Резултати от обработката / Processing Results</h2>
        <div className="header-buttons">
          {onBackToValidation && (
            <button onClick={onBackToValidation} className="back-button">
              ← Назад към валидация / Back to Validation
            </button>
          )}
          <button onClick={onReset} className="reset-button">
            🔄 Нова фактура / New Invoice
          </button>
        </div>
      </div>

      {/* File list for multi-file results */}
      {isMultiFile && filesData.length > 1 && (
        <div className="files-list-container">
          <h3>📁 Обработени файлове / Processed Files ({filesData.length})</h3>
          <div className="files-list">
            {filesData.map((file, index) => (
              <div 
                key={index}
                className={`file-item ${selectedFileIndex === index ? 'selected' : ''}`}
                onClick={() => setSelectedFileIndex(index)}
              >
                <div className="file-icon">
                  {file.metadata?.fileType === 'pdf' ? '📄' : 
                   file.metadata?.fileType === 'image' ? '🖼️' : '📄'}
                </div>
                <div className="file-info">
                  <div className="file-name">{file.fileName}</div>
                  <div className="file-details">
                    <span className="invoice-number">
                      {file.extractedFields?.invoiceNumber || 'No invoice number'}
                    </span>
                    <span className={`confidence ${getConfidenceClass(file.ocrResult?.confidence || 0)}`}>
                      {getConfidenceText(file.ocrResult?.confidence || 0)}
                    </span>
                  </div>
                </div>
                <div className="file-status">
                  {file.extractedFields?.invoiceNumber ? '✅' : '⚠️'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current file details */}
      <div className="current-file-details">
        {isMultiFile && filesData.length > 1 && (
          <div className="file-selector-header">
            <h3>📄 {currentFile.fileName}</h3>
            <div className="file-counter">
              Файл {selectedFileIndex + 1} от {filesData.length} / File {selectedFileIndex + 1} of {filesData.length}
            </div>
          </div>
        )}

        <div className="tab-container">
          <div className="tab-buttons">
            <button 
              className={`tab-button ${activeTab === 'extracted' ? 'active' : ''}`}
              onClick={() => setActiveTab('extracted')}
            >
              🏷️ Данни / Data
            </button>
            <button 
              className={`tab-button ${activeTab === 'ocr' ? 'active' : ''}`}
              onClick={() => setActiveTab('ocr')}
            >
              🔍 OCR Текст / OCR Text
            </button>
            <button 
              className={`tab-button ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => setActiveTab('analysis')}
            >
              📊 Анализ / Analysis
            </button>
            <button 
              className={`tab-button ${activeTab === 'metadata' ? 'active' : ''}`}
              onClick={() => setActiveTab('metadata')}
            >
              ⚙️ Метаданни / Metadata
            </button>
            <button 
              className={`tab-button ${activeTab === 'download' ? 'active' : ''}`}
              onClick={() => setActiveTab('download')}
            >
              📥 Изтегляне / Download
            </button>
          </div>

          <div className="tab-content">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay;
